import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Mock data for demonstration purposes
const mockEmployees = [
  {
    id: 1,
    firstName: 'Emma',
    lastName: 'Johnson',
    email: 'emma.johnson@company.com',
    department: 'Engineering',
    position: 'Senior Developer',
    joiningDate: '2023-04-15',
    status: 'Active',
    salary: 95000,
    avatar: null
  },
  {
    id: 2,
    firstName: 'Michael',
    lastName: 'Chen',
    email: 'michael.chen@company.com',
    department: 'Marketing',
    position: 'Marketing Manager',
    joiningDate: '2022-10-01',
    status: 'Active',
    salary: 85000,
    avatar: null
  },
  {
    id: 3,
    firstName: 'Sarah',
    lastName: 'Williams',
    email: 'sarah.williams@company.com',
    department: 'Finance',
    position: 'Financial Analyst',
    joiningDate: '2023-01-10',
    status: 'On Leave',
    salary: 78000,
    avatar: null
  },
  {
    id: 4,
    firstName: 'David',
    lastName: 'Miller',
    email: 'david.miller@company.com',
    department: 'Operations',
    position: 'Operations Director',
    joiningDate: '2021-06-22',
    status: 'Active',
    salary: 110000,
    avatar: null
  },
  {
    id: 5,
    firstName: 'Jessica',
    lastName: 'Brown',
    email: 'jessica.brown@company.com',
    department: 'Human Resources',
    position: 'HR Specialist',
    joiningDate: '2022-08-15',
    status: 'Active',
    salary: 72000,
    avatar: null
  },
  {
    id: 6,
    firstName: 'Robert',
    lastName: 'Taylor',
    email: 'robert.taylor@company.com',
    department: 'Engineering',
    position: 'Frontend Developer',
    joiningDate: '2023-02-01',
    status: 'Probation',
    salary: 82000,
    avatar: null
  }
];

const EmployeesPage = () => {
  const [employees] = useState(mockEmployees);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [sortConfig, setSortConfig] = useState({
    key: 'lastName',
    direction: 'ascending'
  });

  // Card animation variants for Framer Motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4
      }
    }
  };

  // Filter employees based on search term and selected filters
  const filteredEmployees = employees.filter(employee => {
    const searchMatch = 
      employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.position.toLowerCase().includes(searchTerm.toLowerCase());
    
    const departmentMatch = selectedDepartment === '' || employee.department === selectedDepartment;
    const statusMatch = selectedStatus === '' || employee.status === selectedStatus;
    
    return searchMatch && departmentMatch && statusMatch;
  });

  // Sorting function
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Open quick view for an employee
  const openQuickView = (employee) => {
    setSelectedEmployee(employee);
    setIsQuickViewOpen(true);
  };

  // Close quick view
  const closeQuickView = () => {
    setIsQuickViewOpen(false);
    setTimeout(() => setSelectedEmployee(null), 300);
  };

  // Get all unique departments for the filter dropdown
  const departments = [...new Set(employees.map(emp => emp.department))];
  
  // Get all unique statuses for the filter dropdown
  const statuses = [...new Set(employees.map(emp => emp.status))];

  return (
    <div className="flex min-h-screen bg-gray-50 source-sans-3">
      <div className="flex-1 p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-primary-800 source-sans-3">Employees</h2>
              <p className="text-primary-600 mt-1 source-sans-3">Manage and view all company employees</p>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button 
                className="flex items-center py-2 px-4 bg-primary-600 hover:bg-primary-700 rounded-lg text-white text-sm font-medium transition-colors"
                onClick={() => window.location.href = '/employee/onboarding'}
              >
                <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Employee
              </button>
              <button className="flex items-center py-2 px-4 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg text-primary-700 text-sm font-medium transition-colors">
                <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Employees</p>
                <h4 className="text-2xl font-bold text-gray-800">{employees.length}</h4>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Employees</p>
                <h4 className="text-2xl font-bold text-gray-800">
                  {employees.filter(emp => emp.status === 'Active').length}
                </h4>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">On Leave</p>
                <h4 className="text-2xl font-bold text-gray-800">
                  {employees.filter(emp => emp.status === 'On Leave').length}
                </h4>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">Departments</p>
                <h4 className="text-2xl font-bold text-gray-800">{departments.length}</h4>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg text-primary-800 source-sans-3">
                Employee Directory
              </h3>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setViewMode('grid')} 
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100'} transition-colors`}
                >
                  <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button 
                  onClick={() => setViewMode('table')} 
                  className={`p-2 rounded-lg ${viewMode === 'table' ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100'} transition-colors`}
                >
                  <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className="p-5">
            <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder="Search employees by name, email, position..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex space-x-2">
                <select 
                  className="px-3 py-2 border border-gray-300 rounded-lg source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                >
                  <option value="">All Departments</option>
                  {departments.map((dept, index) => (
                    <option key={index} value={dept}>{dept}</option>
                  ))}
                </select>
                <select 
                  className="px-3 py-2 border border-gray-300 rounded-lg source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="">All Status</option>
                  {statuses.map((status, index) => (
                    <option key={index} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Employees Grid View */}
        {viewMode === 'grid' && (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {sortedEmployees.map((employee) => {
              const initials = (employee.firstName.charAt(0) + employee.lastName.charAt(0)).toUpperCase();
              
              return (
                <motion.div
                  key={employee.id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  variants={cardVariants}
                >
                  <div className="p-6 flex flex-col items-center text-center">
                    <div className="h-20 w-20 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                      {employee.avatar ? (
                        <img src={employee.avatar} alt={`${employee.firstName} ${employee.lastName}`} className="h-20 w-20 rounded-full object-cover" />
                      ) : (
                        <span className="text-primary-800 font-medium text-xl source-sans-3">
                          {initials}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900">{`${employee.firstName} ${employee.lastName}`}</h3>
                    <p className="text-primary-600 font-medium">{employee.position}</p>
                    <p className="text-gray-500 mt-1">{employee.department}</p>
                    
                    <div className="mt-4 w-full">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Status</span>
                        <span className={`px-2 py-1 rounded-full ${
                          employee.status === 'Active' ? 'bg-green-100 text-green-800' : 
                          employee.status === 'On Leave' ? 'bg-amber-100 text-amber-800' : 
                          'bg-primary-100 text-primary-800'
                        }`}>
                          {employee.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-100 p-4">
                    <div className="flex justify-between">
                      <button
                        onClick={() => openQuickView(employee)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium source-sans-3"
                      >
                        View Profile
                      </button>
                      <button className="text-gray-500 hover:text-gray-700 text-sm font-medium source-sans-3">
                        Edit
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Employees Table View */}
        {viewMode === 'table' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3 cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('lastName')}
                    >
                      <div className="flex items-center">
                        Employee
                        {sortConfig.key === 'lastName' && (
                          <svg 
                            className={`ml-1 w-4 h-4 ${sortConfig.direction === 'ascending' ? 'transform rotate-180' : ''}`} 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th 
                      className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3 cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('department')}
                    >
                      <div className="flex items-center">
                        Department
                        {sortConfig.key === 'department' && (
                          <svg 
                            className={`ml-1 w-4 h-4 ${sortConfig.direction === 'ascending' ? 'transform rotate-180' : ''}`} 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th 
                      className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3 cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('position')}
                    >
                      <div className="flex items-center">
                        Position
                        {sortConfig.key === 'position' && (
                          <svg 
                            className={`ml-1 w-4 h-4 ${sortConfig.direction === 'ascending' ? 'transform rotate-180' : ''}`} 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th 
                      className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3 cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('joiningDate')}
                    >
                      <div className="flex items-center">
                        Joining Date
                        {sortConfig.key === 'joiningDate' && (
                          <svg 
                            className={`ml-1 w-4 h-4 ${sortConfig.direction === 'ascending' ? 'transform rotate-180' : ''}`} 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th 
                      className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3 cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('salary')}
                    >
                      <div className="flex items-center">
                        Salary
                        {sortConfig.key === 'salary' && (
                          <svg 
                            className={`ml-1 w-4 h-4 ${sortConfig.direction === 'ascending' ? 'transform rotate-180' : ''}`} 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th 
                      className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3 cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        {sortConfig.key === 'status' && (
                          <svg 
                            className={`ml-1 w-4 h-4 ${sortConfig.direction === 'ascending' ? 'transform rotate-180' : ''}`} 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedEmployees.map((employee) => {
                    const initials = (employee.firstName.charAt(0) + employee.lastName.charAt(0)).toUpperCase();
                    return (
                      <tr key={employee.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <span className="text-primary-800 font-medium text-sm source-sans-3">
                                  {initials}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 source-sans-3">
                                {`${employee.firstName} ${employee.lastName}`}
                              </div>
                              <div className="text-sm text-gray-500 source-sans-3">
                                {employee.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 source-sans-3">{employee.department}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 source-sans-3">{employee.position}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 source-sans-3">{new Date(employee.joiningDate).toLocaleDateString()}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 source-sans-3">${employee.salary.toLocaleString()}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full source-sans-3 ${
                            employee.status === 'Active' ? 'bg-green-100 text-green-800' : 
                            employee.status === 'On Leave' ? 'bg-amber-100 text-amber-800' : 
                            'bg-primary-100 text-primary-800'
                          }`}>
                            {employee.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button 
                            onClick={() => openQuickView(employee)}
                            className="text-primary-600 hover:text-primary-800 mr-3 source-sans-3"
                          >
                            View
                          </button>
                          <button className="text-gray-500 hover:text-gray-700 source-sans-3">
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {sortedEmployees.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
              <svg className="w-8 h-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No employees found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filter criteria</p>
            <button 
              onClick={() => {
                setSearchTerm('');
                setSelectedDepartment('');
                setSelectedStatus('');
              }}
              className="py-2 px-4 bg-primary-600 hover:bg-primary-700 rounded-lg text-white text-sm font-medium transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Quick View Modal */}
        {isQuickViewOpen && selectedEmployee && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div 
                className="fixed inset-0 transition-opacity" 
                aria-hidden="true"
                onClick={closeQuickView}
              >
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <motion.div 
                className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-primary-50 px-4 py-5 border-b border-gray-200 sm:px-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Employee Profile
                    </h3>
                    <button
                      onClick={closeQuickView}
                      className="bg-primary-50 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center">
                    <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center mr-4">
                      <span className="text-primary-800 font-medium text-xl source-sans-3">
                        {(selectedEmployee.firstName.charAt(0) + selectedEmployee.lastName.charAt(0)).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}</h3>
                      <p className="text-primary-600">{selectedEmployee.position}</p>
                    </div>
                  </div>

                  <div className="mt-6 border-t border-gray-200 pt-6">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Department</dt>
                        <dd className="mt-1 text-sm text-gray-900">{selectedEmployee.department}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Status</dt>
                        <dd className="mt-1">
                          <span className={`px-2 py-1 text-xs inline-flex items-center rounded-full font-medium ${
                            selectedEmployee.status === 'Active' ? 'bg-green-100 text-green-800' : 
                            selectedEmployee.status === 'On Leave' ? 'bg-amber-100 text-amber-800' : 
                            'bg-primary-100 text-primary-800'
                          }`}>
                            {selectedEmployee.status}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Email</dt>
                        <dd className="mt-1 text-sm text-gray-900">{selectedEmployee.email}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Salary</dt>
                        <dd className="mt-1 text-sm text-gray-900">${selectedEmployee.salary.toLocaleString()}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Joining Date</dt>
                        <dd className="mt-1 text-sm text-gray-900">{new Date(selectedEmployee.joiningDate).toLocaleDateString()}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => window.location.href = `/employee/${selectedEmployee.id}/edit`}
                  >
                    Edit Employee
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={closeQuickView}
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeesPage;